import classcat from "classcat";
import React, { SyntheticEvent } from "react";

import { Button, IconBasket } from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import Price from "@is/components/Price";
import { MediaVariants } from "@is/constants";
import { MediaOrderingRule, ProductItem, WidgetType } from "@is/types";
import { sortProductMedia } from "@is/utils";

import css from "./WidgetProductItem.module.css";
import useManageWidgetProductItem from "./hooks/useManageWidgetProductItem";

type Props = {
  item: ProductItem;
  animationDelayIndex: number;
  onClick: (item: ProductItem) => void;
  onQuickAddToBasket: (item: ProductItem) => void;
  quickAddToBasketItem: ProductItem | null;
  onQuickAddToBasketRemoveItem: () => void;
  quickAddToBasketMode: boolean;
  onQuickAddToBasketLayerClick: (event: SyntheticEvent) => void;
  widgetType: WidgetType;
};

const WidgetProductItem = ({
  item,
  animationDelayIndex,
  onClick,
  onQuickAddToBasket,
  quickAddToBasketItem,
  onQuickAddToBasketRemoveItem,
  quickAddToBasketMode,
  onQuickAddToBasketLayerClick,
  widgetType,
}: Props) => {
  const {
    selectedColorVariantId,
    selectedSize,
    selectedLength,
    sizes,
    lengths,
    handleSelectLength,
    handleSelectSize,
    handleSelectVariant,
    variants,
    handleClick,
    handleQuickAddToBasket,
    mediaUrl,
    ref,
    basketButtonContent,
    handleBasketButtonClick,
    isAdded,
    showOutOfStockNotification,
    isLengthRequired,
    variantsWithSelectedSize,
    sizesForColorVariant,
    lengthsForColorVariant,
    showUnavailableSlash,
    colorName,
    isBasketItemsPending,
    t,
  } = useManageWidgetProductItem({
    item,
    quickAddToBasketItem,
    onQuickAddToBasket,
    onClick,
    onQuickAddToBasketRemoveItem,
    quickAddToBasketMode,
    widgetType,
  });

  return (
    <div
      style={{ ["--index" as string]: animationDelayIndex }}
      className={css.container}
      onClick={handleClick}
      ref={ref}
    >
      <div className={css.imageContainer}>
        {quickAddToBasketItem && (
          <div className={css.layer} onClick={onQuickAddToBasketLayerClick}>
            {variants.length > 1 && (
              <div
                className={css.colorVariantSelector}
                style={{ backgroundColor: "rgba(254, 254, 255, 0.9)" }}
              >
                {variants?.map((variant) => (
                  <div
                    key={variant.id}
                    className={classcat([
                      css.colorVariant,
                      {
                        [css.selectedColorVariant]:
                          selectedColorVariantId === variant.id,
                      },
                    ])}
                    onClick={handleSelectVariant}
                    data-id={variant.id}
                  >
                    <Image
                      mediavariant={MediaVariants.THUMBNAIL}
                      src={
                        sortProductMedia(
                          variant.media,
                          MediaOrderingRule.Widget
                        )[0]?.url
                      }
                      layout="cover"
                    />
                    {showUnavailableSlash &&
                    !variantsWithSelectedSize.includes(variant.id) ? (
                      <div className={css.diagonal} />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {!!sizes.length && (
              <div
                className={css.sizeAndLengthSelector}
                style={{ backgroundColor: "rgba(254, 254, 255, 0.9)" }}
              >
                {sizes.length && lengths.length ? (
                  <div className={css.selectorPlaceholder}>{t("Size")}</div>
                ) : null}
                {sizes.map((size) => (
                  <div
                    key={size}
                    className={classcat([
                      css.sizeAndLength,
                      {
                        [css.selectedSizeAndLength]: selectedSize === size,
                        [css.unavailableSizeAndLength]:
                          !sizesForColorVariant.includes(size),
                        [css.fullWidth]: sizes.length === 1,
                      },
                    ])}
                    onClick={handleSelectSize}
                    data-id={size}
                  >
                    {size}
                  </div>
                ))}
              </div>
            )}
            {!!lengths.length && (
              <div
                className={css.sizeAndLengthSelector}
                style={{ backgroundColor: "rgba(254, 254, 255, 0.9)" }}
              >
                {sizes.length && lengths.length ? (
                  <div className={css.selectorPlaceholder}>{t("Length")}</div>
                ) : null}
                {lengths.map((length) => (
                  <div
                    key={length}
                    className={classcat([
                      css.sizeAndLength,
                      {
                        [css.selectedSizeAndLength]: selectedLength === length,
                        [css.unavailableSizeAndLength]:
                          !lengthsForColorVariant.includes(length),
                        [css.fullWidth]: lengths.length === 1,
                      },
                    ])}
                    onClick={handleSelectLength}
                    data-id={length}
                  >
                    {length}
                  </div>
                ))}
              </div>
            )}
            <div className={css.basketButtonContainer}>
              <Button
                width="full"
                sizing="sm"
                className={classcat([
                  css.basketButton,
                  {
                    [css.added]: isAdded,
                    [css.outOfStock]: showOutOfStockNotification,
                    [css.notSelectedSizeOrLength]:
                      !selectedSize || (isLengthRequired && !selectedLength),
                  },
                ])}
                onClick={handleBasketButtonClick}
                state={isBasketItemsPending ? "pending" : undefined}
              >
                {basketButtonContent}
              </Button>
            </div>
          </div>
        )}
        <Image
          style={{ overflowClipMargin: "unset" }}
          layout="cover"
          mediavariant={MediaVariants.MEDIUM}
          src={mediaUrl}
        />
        {!quickAddToBasketItem && (
          <button
            className={classcat([
              css.basketIconContainer,
              "text-label-base",
              { [css.hidden]: widgetType !== WidgetType.HowAboutThese },
            ])}
            onClick={handleQuickAddToBasket}
          >
            <IconBasket />
          </button>
        )}
      </div>
      <div className={css.productInfo}>
        <div
          style={{ ["--index" as string]: animationDelayIndex }}
          className={css.title}
        >
          {item.name}
        </div>
        {!!colorName?.length && (
          <div className={css.colorName}>{colorName}</div>
        )}
        {!!(item.price?.retailPrice && item.price?.currencyCode) && (
          <Price price={item.price} />
        )}
      </div>
    </div>
  );
};

export default WidgetProductItem;
